import { LoginCallback } from '@okta/okta-react'
import { Suspense } from 'react'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router'
import { AccountLink } from '~/entities/user'
import { useLogout } from '~/features/auth/logout'
import { UploadProgressPopup } from '~/features/document-upload'
import { AgentRunPage } from '~/pages/agent-run'
import { AgentRunsPage } from '~/pages/agent-runs'
import { ConsignmentPage } from '~/pages/consignment'
import { ConsignmentClusteringPage } from '~/pages/consignment-clustering'
import { ConsignmentsPage, useFilters as useConsignmentsFilters } from '~/pages/consignments'
import { DocumentPage } from '~/pages/document'
import { DocumentClassificationPage } from '~/pages/document-classification'
import { DocumentDataExtractionPage } from '~/pages/document-data-extraction'
import { DocumentDuplicationPage } from '~/pages/document-duplication'
import { DocumentEobProcessingPage } from '~/pages/document-eob-processing'
import {
  DocumentPatientMatchingPage,
  PatientMatchingLoadingSkeleton,
} from '~/pages/document-patient-matching'
import { DocumentPaymentDepositPage } from '~/pages/document-payment-deposit'
import { DocumentPdfViewPage } from '~/pages/document-pdf-view'
import { DocumentTaggingPage } from '~/pages/document-tagging'
import { DocumentsPage, useFilters as useDocumentsFilters } from '~/pages/documents'
import { GoldSetQueuePage } from '~/pages/gold-set-queue'
import { GoldSetReviewPage } from '~/pages/gold-set-review'
import { LoginPage } from '~/pages/login'
import { LoginErrorPage } from '~/pages/login-error'
import { MedistreamReportPage, useReportAccess } from '~/pages/medistream-report'
import { NotFoundPage } from '~/pages/not-found'
import { VersionPage } from '~/pages/version'
import { MainLayout } from '~/shared/ui'
import { MainPageSubheader } from '~/widgets/main-page-subheader'
import { AppErrorBoundary } from './error-boundary'
import { FlagGate } from './flag-gate'
import { PageBase } from './page-base'
import { AuthSecurity, SecureRoute } from './providers/auth-security'

function MainPageSubheaderWrapper() {
  const { resetToInitialFilters: resetDocumentsFilters } = useDocumentsFilters()
  const { resetToInitialFilters: resetConsignmentsFilters } = useConsignmentsFilters()

  return (
    <MainPageSubheader
      resetDocumentsFilters={resetDocumentsFilters}
      resetConsignmentsFilters={resetConsignmentsFilters}
    />
  )
}

/**
 * The Medistream report is intentionally not linked anywhere in the UI. Users
 * without the flag or without onshore status get the same 404 as a nonexistent
 * route, so the page's existence is never revealed. Composed here rather than
 * inside the page because FSD forbids a page importing another page.
 */
function MedistreamReportRoute() {
  const { isLoading, isAllowed } = useReportAccess()

  if (isLoading) {
    return null
  }

  return isAllowed ? <MedistreamReportPage /> : <NotFoundPage />
}

/**
 * Not under PageBase, whose hooks (reviewer lookup, work-queue filters) these pages don't need.
 * The 404 for users without the flag keeps the app header, like the Medistream report.
 */
function AgentRunsLayout() {
  const { logout } = useLogout()

  return (
    <SecureRoute>
      <MainLayout accountLink={<AccountLink logout={logout} />}>
        <FlagGate flag="2026-10-document-agent-viewer">
          <Outlet />
        </FlagGate>
      </MainLayout>
    </SecureRoute>
  )
}

/**
 * No app header on purpose: its logo links to the work queue, which shows system categories,
 * and the gold-set review must stay blind to them.
 */
function GoldSetLayout() {
  return (
    <SecureRoute>
      <div className="flex h-dvh flex-col overflow-hidden">
        <FlagGate flag="2026-10-gold-set-review">
          <Outlet />
        </FlagGate>
      </div>
    </SecureRoute>
  )
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <AuthSecurity>
        <AppErrorBoundary>
          <Routes>
            <Route
              path="login"
              element={<LoginPage />}
            />
            <Route
              path="login/callback"
              element={<LoginCallback errorComponent={LoginErrorPage} />}
            />
            <Route element={<PageBase />}>
              <Route element={<MainPageSubheaderWrapper />}>
                <Route
                  index
                  element={<DocumentsPage />}
                />
                <Route
                  path="consignments"
                  element={<ConsignmentsPage />}
                />
              </Route>
            </Route>
            <Route element={<PageBase />}>
              <Route path="consignments">
                <Route
                  path=":consignmentKey"
                  element={<ConsignmentPage />}
                >
                  <Route
                    path="clustering"
                    element={<ConsignmentClusteringPage />}
                  />
                </Route>
              </Route>
            </Route>
            <Route element={<PageBase />}>
              <Route
                path="version"
                element={<VersionPage />}
              />
            </Route>
            <Route element={<PageBase />}>
              <Route
                path="medistream-report"
                element={<MedistreamReportRoute />}
              />
            </Route>
            <Route element={<AgentRunsLayout />}>
              <Route path="agent-runs">
                <Route
                  index
                  element={<AgentRunsPage />}
                />
                <Route
                  path=":documentKey"
                  element={<AgentRunPage />}
                />
              </Route>
            </Route>
            <Route element={<GoldSetLayout />}>
              <Route path="gold-set">
                <Route
                  index
                  element={<GoldSetQueuePage />}
                />
                <Route
                  path=":documentKey"
                  element={<GoldSetReviewPage />}
                />
              </Route>
            </Route>
            <Route
              path="documents/:documentKey/view-pdf"
              element={
                <SecureRoute>
                  <Suspense>
                    <DocumentPdfViewPage />
                  </Suspense>
                </SecureRoute>
              }
            />
            <Route element={<PageBase />}>
              <Route path="documents">
                <Route
                  path=":documentKey"
                  element={<DocumentPage />}
                >
                  <Route
                    path="classification"
                    element={<DocumentClassificationPage />}
                  />
                  <Route
                    path="data-extraction"
                    element={<DocumentDataExtractionPage />}
                  />
                  <Route
                    path="tagging"
                    element={<DocumentTaggingPage />}
                  />
                  <Route
                    path="payment-deposit"
                    element={<DocumentPaymentDepositPage />}
                  />
                  <Route
                    path="duplication"
                    element={<DocumentDuplicationPage />}
                  />
                  <Route
                    path="eob-processing"
                    element={<DocumentEobProcessingPage />}
                  />
                  <Route
                    path="matching"
                    element={
                      <Suspense fallback={<PatientMatchingLoadingSkeleton />}>
                        <DocumentPatientMatchingPage />
                      </Suspense>
                    }
                  />
                </Route>
              </Route>
            </Route>
            <Route element={<PageBase />}>
              <Route
                path="*"
                element={<NotFoundPage />}
              />
            </Route>
          </Routes>
          <UploadProgressPopup />
        </AppErrorBoundary>
      </AuthSecurity>
    </BrowserRouter>
  )
}
